import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';

import { FikoScreenshot } from '../IFikoThreeJS/fiko-screenshot/FikoScreenshot';
/**
 * 💪 STP/WRL Önizleme Kartı — kart listelerinde (icerikler vb.) STEP/WRL dosyaları için
 * hafif, lazy-instantiate 3D önizleme. Kullanıcı "3D Önizle" butonuna basana kadar
 * ne OCCT-WASM parse'ı ne de WebGL context açılmaz — bir listede çok sayıda 3D dosyalı
 * kart olsa bile tarayıcının WebGL context limitine (~8-16) takılmayız.
 *
 * Kapat butonuna basıldığında (ya da bileşen yok edildiğinde) alttaki gerçek
 * <fikoyt-3d [gomulu]="true"> instance'ı destroy edilir → context serbest kalır.
 */
@Component({
  selector: 'fikoyt-stp-onizleme-karti',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="stp-onizleme-kutu" [class.acik]="acik" [style.height.px]="acik ? null : '100%'">
      <ng-container #konteynir></ng-container>

      @if (!acik) {
        @if (fotografUrl) {
          <img class="stp-sunucu-resmi" [src]="fotografUrl" alt="Çekilen STP görüntüsü" />
        } @else if (resimUrl) {
          <img class="stp-sunucu-resmi" [src]="resimUrl" alt="STP görüntüsü" loading="lazy" />
        }
        <button type="button" class="stp-onizle-buton" [class.yukleniyor]="yukleniyor" (click)="onizlemeAc()">
          @if (yukleniyor) {
            <i class="icon spinner loading"></i><span>Yükleniyor…</span>
          } @else {
            <img src="/simgeler/3d.png" class="stp-onizle-ikon" alt="" />
            <span> Önizle</span>
          }
        </button>
      } @else {
        <button type="button" class="stp-kapat-buton" title="Önizlemeyi kapat" (click)="onizlemeKapat()">
          <i class="icon close"></i>
        </button>

        @if (fotoCekebilir) {
          <button type="button" class="stp-foto-cek-buton" [class.yukleniyor]="fotoCekiliyor || yukleniyorFoto" (click)="fotoCek()" title="Fotoğraf çek & kalıcı yükle">
            <i class="icon camera"></i>
          </button>
        }

        @if (dosyaIndiriliyor) {
          <div class="stp-progress-kutu">
            <div class="stp-progress-bar">
              <div class="stp-progress-fill" [style.width.%]="indirmeProgress"></div>
            </div>
            <span class="stp-progress-mesaj">{{ indirmeProgressMesaj }}</span>
          </div>
        }
      }
    </div>
  `,
  styles: [`
    .stp-onizleme-kutu {
      position: absolute;
      width: 100%;
      background: #11161d;
      border-radius: 8px;
      overflow: hidden;
      top:0px;
      height:100%;
    }
    /* 3D açıkken parent yüksekliğini doldur */
    .stp-onizleme-kutu.acik {
      height: 100%;
      min-height: 180px;
    }
    /* Sunucudan gelen kalıcı görüntü — kapalı kartın zemini */
    .stp-sunucu-resmi {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: #0d1217;
      z-index: 0;
    }
    .stp-onizle-buton {
      position: absolute;
      justify-content: center;
      gap: 6px;
      background: rgba(10, 132, 192, 0.08);
      border: 1px dashed rgba(10, 132, 192, 0.4);
      color: #0a84c0;
      cursor: pointer;
      font: 600 12px system-ui;
      z-index: 1;
      right: 0;
      bottom: 0;
    }
    .stp-onizle-buton:hover { background: rgba(10, 132, 192, 0.16); }
    .stp-onizle-buton.yukleniyor { cursor: wait; color: #7db0ff; }
    .stp-onizle-ikon { width: 22px; height: 22px; object-fit: contain; }
    .stp-kapat-buton {
      position: absolute;
      top: 6px;
      right: 6px;
      z-index: 5;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: none;
      background: rgba(0, 0, 0, 0.55);
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .stp-kapat-buton:hover { background: rgba(0, 0, 0, 0.8); }
    .stp-foto-cek-buton {
      position: absolute;
      bottom: 8px;
      right: 8px;
      z-index: 6;
      width: 34px;
      height: 34px;
      border-radius: 50%;
      border: none;
      background: rgba(0, 0, 0, 0.55);
      color: #fff;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      font-size: 16px;
    }
    .stp-foto-cek-buton:hover { background: rgba(0, 0, 0, 0.8); }
    .stp-foto-cek-buton.yukleniyor { cursor: wait; opacity: 0.7; }
    .stp-foto-kapak {
      position: absolute;
      bottom: 8px; left: 8px;
      z-index: 2;
      display: flex; align-items: center; gap: 6px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 6px;
      padding: 4px 8px;
      cursor: pointer; max-width: 60%;
    }
    .stp-foto-kapak:hover { background: rgba(0, 0, 0, 0.85); }
    .stp-foto-kapak img { width: 42px; height: 30px; object-fit: contain; border-radius: 4px; background: #000; }
    .stp-foto-kapak-bilgi { color: #e8e8e8; font: 500 11px system-ui; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    /* Progress bar — STP yükleme durumu */
    .stp-progress-kutu {
      position: absolute;
      bottom: 12px;
      left: 12px;
      right: 12px;
      z-index: 10;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .stp-progress-bar {
      width: 100%;
      height: 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
      overflow: hidden;
    }
    .stp-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #0a84c0, #00d4ff);
      border-radius: 3px;
      transition: width 0.3s ease;
    }
    .stp-progress-mesaj {
      color: #a0c4d8;
      font: 500 11px system-ui;
      text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    }
  `],
})
export class FikoStpOnizlemeKartiComponent implements OnDestroy {
  /** Önizlenecek STEP/WRL dosyasının backend dosya id'si */
  @Input() dosyaId: string;
  /** Henüz sunucuya yüklenmemiş yerel dosya (paylaş kutusu, upload öncesi). Varsa dosyaId yerine
   *  bu kullanılır — sunucudan indirme yapılmadan doğrudan tarayıcıdaki File 3D'ye yüklenir. */
  @Input() localDosya: File | null = null;
  /** Kalıcı çekilmiş görüntünün sunucu URL'i (varsa kapalı kartın zemininde gösterilir) */
  @Input() resimUrl: string | null = null;
  /** Fotoğraf çekme yalnızca kart sahibi veya süper yöneticide (buton + yükleme kapakçığı) */
  @Input() fotoCekebilir = false;
  /** Kapalıyken önizleme butonunun yüksekliği (px); açıkken parent'ı %100 doldurur */
  @Input() yukseklik: number = 220;
  /** Önizleme açıldı/kapandı — kart, çevresindeki diğer öğeleri gizleyip alanı 3D'ye bırakmak için dinleyebilir */
  @Output() acikDegisti = new EventEmitter<boolean>();

  /** static:true — @if bloğunun dışında, her zaman DOM'da → ViewChild race condition yok */
  @ViewChild('konteynir', { read: ViewContainerRef, static: true }) konteynir: ViewContainerRef;

  acik = false;
  yukleniyor = false;
  private viewerRef: ComponentRef<any> | null = null;

  /** Son çekilen fotoğrafın önizleme URL'i (varsa kapalıyken gösterilir, buton her zaman durur) */
  fotografUrl: string | null = null;
  /** Fotoğraf çekilirken kilit */
  fotoCekiliyor = false;
  /** Görsel sunucuya yüklenirken kilit */
  yukleniyorFoto = false;
  private fotografBlob: Blob | null = null;
  /** Çekilen görüntü blob'unu üst kata iletir; üst katman guncelleme isteğiyle multipart yükleyip ek.resimid yazar */
  @Output() fotografYuklendi = new EventEmitter<Blob>();

  /** Dosya indirme ilerlemesi (0-100) */
  indirmeProgress = 0;
  /** Dosya indirme mesajı */
  indirmeProgressMesaj = '';
  /** Dosya indirme durumu */
  dosyaIndiriliyor = false;

  constructor(private cd: ChangeDetectorRef) {}

  async onizlemeAc(): Promise<void> {
    if (this.acik || this.yukleniyor || (!this.dosyaId && !this.localDosya)) return;
    this.yukleniyor = true;
    this.cd.markForCheck();
    try {
      // Ağır bileşen (OCCT-WASM + fikoyt-3d motoru) yalnızca tıklandığında indirilir
      const { FikoYT3DComponent } = await import('../Fiko3DComponent/.component');
      this.viewerRef = this.konteynir.createComponent(FikoYT3DComponent);
      this.viewerRef.instance.gomulu = true;
      // localDosya varsa (henüz upload edilmemiş) öncelikli — sunucudan indirme atlanır
      this.viewerRef.instance.localDosya = this.localDosya;
      this.viewerRef.instance.dosyaId = this.dosyaId;

      // Dosya indirme progress event'ini dinle
      this.viewerRef.instance.dosyaIndirmeProgress.subscribe((event: { mesaj: string; progress: number }) => {
        this.indirmeProgress = event.progress;
        this.indirmeProgressMesaj = event.mesaj;
        this.dosyaIndiriliyor = event.progress < 100 && !event.mesaj.startsWith('Hata');
        this.cd.markForCheck();
      });

      this.acik = true;
      this.acikDegisti.emit(true);
      // 💪 FIX: Canvas DOM'a eklendikten sonra layout henüz hesaplanmamış olabilir.
      // Bir frame bekleyip resize tetikleyerek renderer'ın doğru boyutu okumasını sağla.
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    } finally {
      this.yukleniyor = false;
      this.cd.markForCheck();
    }
  }

  onizlemeKapat(): void {
    this.viewerRef?.destroy();
    this.viewerRef = null;
    this.acik = false;
    this.acikDegisti.emit(false);
    // Progress durumunu sıfırla
    this.indirmeProgress = 0;
    this.indirmeProgressMesaj = '';
    this.dosyaIndiriliyor = false;
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    this.viewerRef?.destroy();
    this.viewerRef = null;
  }
/** 3D viewport'tan screenshot alır, önizleme gösterir ve sunucuya kalıcı yükler */
  async fotoCek(): Promise<void> {
    if (this.fotoCekiliyor || !this.fotoCekebilir) return;
    const viewer = this.viewerRef?.instance as any;
    const motor = viewer?.fikoThreejs;
    const renderer = motor?.sunucu;
    const sahne = motor?.sahneler?.[0];
    const kamera = motor?.aktifKamera;
    if (!renderer || !sahne || !kamera) {
      console.warn('[STP] 3D motor hazır değil');
      return;
    }
    this.fotoCekiliyor = true;
    this.cd.markForCheck();
    try {
      const ss = new FikoScreenshot(renderer, sahne, kamera);
      const sonuc = await ss.capture({ format: 'png', scale: 2, transparent: false });
      this.fotografBlob = sonuc.blob;
      this.fotografUrl = sonuc.dataUrl;
      // Yükleme üst katmanda yapılır: guncelleme isteği + multipart (ek.resimid deseni)
      this.fotografYuklendi.emit(sonuc.blob);
    } catch (e) {
      console.error('[STP] Fotoğraf çekme hatası:', e);
    } finally {
      this.fotoCekiliyor = false;
      this.cd.markForCheck();
    }
  }

  /** Çekilen görseli PNG olarak indirir */
  fotografKaydet(): void {
    if (!this.fotografUrl) return;
    const a = document.createElement('a');
    a.href = this.fotografUrl;
    a.download = 'stp-goruntu.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /** Çekilen görseli panoya kopyalar */
  async fotografKopyala(): Promise<void> {
    if (!this.fotografBlob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': this.fotografBlob })]);
    } catch (e) {
      console.warn('[STP] Panoya kopyalanamadı:', e);
    }
  }
}

